/**
 * Payment Transactions Service
 *
 * Data access layer for payment transaction operations.
 * Handles fetching payment history and recording manual payments via API.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';

export type TransactionType = 'booking_payment' | 'damage_charge';

export interface PaymentTransaction {
  id: string;
  booking_id: string;
  merchant_id: string;
  amount: number;
  currency: string;
  channel: string;
  transaction_type: TransactionType;
  stripe_payment_intent_id: string | null;
  stripe_checkout_session_id: string | null;
  notes: string | null;
  recorded_by: string | null;
  created_at: string;
}

export interface RecordManualPaymentParams {
  bookingId: string;
  amount: number;
  channel: string;
  notes?: string;
  userId?: string;
  /**
   * Record the ledger row without touching bookings.amount_paid or payment_state.
   * Only for callers that have already set both — currently the admin new-booking
   * flow, which writes them at creation from the operator's chosen payment method.
   */
  skipBookingUpdate?: boolean;
}

export interface CreatePaymentLinkParams {
  bookingId: string;
  amount: number;
}

export interface ChargeOffSessionParams {
  bookingId: string;
  amount: number;
  reason: string;
  clientNonce: string;
}

export interface ChargeOffSessionSuccess {
  status: 'succeeded';
  paymentIntentId: string;
  transaction: PaymentTransaction;
}

export interface ChargeOffSessionRequiresAction {
  status: 'requires_action';
  paymentIntentId: string | null;
  nextAction: unknown;
  message: string;
}

export type ChargeOffSessionResult = ChargeOffSessionSuccess | ChargeOffSessionRequiresAction;

export const PAYMENT_CHANNELS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'stripe_card', label: 'Stripe / Card' },
  { value: 'stripe_link', label: 'Stripe Link' },
  { value: 'stripe_ideal', label: 'iDEAL' },
  { value: 'stripe_bancontact', label: 'Bancontact' },
  { value: 'stripe_other', label: 'Stripe (Other)' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'manual', label: 'Manual / Other' },
  { value: 'other', label: 'Other' },
] as const;

export function getChannelLabel(channel: string): string {
  const found = PAYMENT_CHANNELS.find(c => c.value === channel);
  return found?.label || channel;
}

/**
 * Fetch payment transaction history for a booking
 */
export async function getPaymentTransactions(bookingId: string) {
  try {
    const { data, error } = await supabase
      .from('payment_transactions')
      .select('*')
      .eq('booking_id', bookingId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return { data: (data || []) as PaymentTransaction[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Record a manual payment via the API endpoint
 */
export async function recordManualPayment(params: RecordManualPaymentParams) {
  try {
    const response = await fetch('/api/stripe/record-payment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return { data: null, error: result.message || 'Failed to record payment' };
    }

    return {
      data: {
        transaction: result.transaction as PaymentTransaction,
        newPaymentState: result.newPaymentState as string,
        newAmountPaid: result.newAmountPaid as number,
      },
      error: null,
    };
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'Network error' };
  }
}

/**
 * Compute the total amount paid via Stripe (with a stripe_payment_intent_id)
 */
export function getStripeTransactionTotal(transactions: PaymentTransaction[]): number {
  return transactions
    .filter(tx => tx.channel.startsWith('stripe_') && tx.stripe_payment_intent_id)
    .reduce((sum, tx) => sum + tx.amount, 0);
}

/**
 * Compute the total amount paid via non-Stripe methods (PayPal, cash, bank transfer, etc.)
 */
export function getNonStripeTransactionTotal(transactions: PaymentTransaction[]): number {
  return transactions
    .filter(tx => !tx.channel.startsWith('stripe_') || !tx.stripe_payment_intent_id)
    .reduce((sum, tx) => sum + tx.amount, 0);
}

/**
 * Create a Stripe payment link (Checkout Session) for outstanding amount
 */
export async function createPaymentLink(params: CreatePaymentLinkParams) {
  try {
    const response = await fetch('/api/stripe/create-payment-link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return { data: null, error: result.message || 'Failed to create payment link' };
    }

    return {
      data: {
        checkoutUrl: result.checkoutUrl as string,
        sessionId: result.sessionId as string,
      },
      error: null,
    };
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'Network error' };
  }
}

/**
 * Charge a saved card off-session for damage / loss / late-return / cleaning fees.
 *
 * Sends the caller's Supabase JWT in the Authorization header. The endpoint
 * verifies the JWT, requires `profiles.is_platform_admin = true`, and ignores
 * any user identity supplied in the body — the auth user is recorded as
 * `recorded_by` and as the audit-log actor.
 *
 * On success returns the inserted payment_transactions row.
 * On `authentication_required` returns next_action so the UI can prompt the
 * customer to confirm via Stripe's hosted page.
 */
export async function chargeOffSession(params: ChargeOffSessionParams) {
  try {
    const { data: sessionResult } = await supabase.auth.getSession();
    const token = sessionResult.session?.access_token;
    if (!token) {
      return { data: null, error: 'Not signed in' };
    }

    const response = await fetch('/api/stripe/charge-off-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(params),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return { data: null, error: result.message || 'Failed to charge saved card' };
    }

    if (result.status === 'requires_action') {
      return {
        data: {
          status: 'requires_action' as const,
          paymentIntentId: result.paymentIntentId as string | null,
          nextAction: result.nextAction,
          message: result.message as string,
        },
        error: null,
      };
    }

    return {
      data: {
        status: 'succeeded' as const,
        paymentIntentId: result.paymentIntentId as string,
        transaction: result.transaction as PaymentTransaction,
      },
      error: null,
    };
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'Network error' };
  }
}
